import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import dayjs from 'dayjs';

const RoleForm = ({ onSubmit, editingRole, onCancel, roles = [], formSuccess = '', formError = '', setFormSuccess, setFormError, users, resetKey }) => {
  const [form, setForm] = useState({ role_name: '', description: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editingRole) setForm(editingRole);
    else setForm({ role_name: '', description: '' });
    setErrors({});
    if (setFormSuccess) setFormSuccess('');
    if (setFormError) setFormError('');
  }, [editingRole, resetKey]);

  const validate = () => {
    const newErrors = {};
    if (!form.role_name.trim()) newErrors.role_name = 'Please enter a role name.';
    setErrors(newErrors);
    if (setFormError) setFormError('');
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: undefined });
    if (setFormError) setFormError('');
    if (setFormSuccess) setFormSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (!validate()) return;
      onSubmit(form);
      if (!editingRole) setForm({ role_name: '', description: '' });
      setErrors({});
      if (setFormSuccess) setFormSuccess(editingRole ? 'Role updated successfully!' : 'Role added successfully!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setForm({ role_name: '', description: '' });
    setErrors({});
    if (setFormSuccess) setFormSuccess('');
    if (setFormError) setFormError('');
  };

  // Helper to get user display name and role
  const getUserDisplay = (userId) => {
    // if (!userId || !Array.isArray(users)) return 'Unknown';
    // const user = users.find(u => String(u.user_id) === String(userId));
    // if (!user) return 'Unknown';
    // return `${user.username} (${user.role_name || 'Unknown'})`;
    if (!userId) return 'Unknown';
    const user = users.find(u => String(u.user_id) === String(userId));
    if (!user) return 'Unknown';
    return `${user.username} (${user.role_name || 'Unknown'})`;
  };

  // Helper to format date
  const formatDate = (date) => date ? dayjs(date).format('DD-MM-YYYY hh:mm A') : 'Unknown';

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 400, width: '100%', mx: 'auto', mt: 1, p: 0 }} autoComplete="off">
      <Box sx={{ fontWeight: 700, fontSize: 18, color: '#111', mb: 2.5, letterSpacing: 0.7, textAlign: 'left', width: '100%' }}>
        {editingRole ? 'Edit Role' : 'Add Role'}
      </Box>
      <Stack spacing={2} alignItems="center">
        <TextField
          label="Role Name*"
          name="role_name"
          value={form.role_name}
          onChange={handleChange}
          error={!!errors.role_name}
          helperText={errors.role_name}
          fullWidth
          inputProps={{ maxLength: 50 }}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#111',
              minHeight: 45,
              height: 45,
              '& fieldset': { borderColor: !!errors.role_name ? '#D32F2F' : '#BDBDBD' },
              '&:hover fieldset': { borderColor: !!errors.role_name ? '#D32F2F' : '#BDBDBD' },
              '&.Mui-focused fieldset': { borderColor: !!errors.role_name ? '#D32F2F' : '#00C853' },
            },
            mb: 1.5,
            '& label.Mui-focused': { color: '#00C853' },
          }}
          InputProps={{
            sx: {
              background: '#fff',
              boxShadow: 'none',
              padding: 0,
              minHeight: 45,
              height: 45,
              '& input': {
                background: '#fff !important',
                boxShadow: 'none !important',
                padding: '0 8px',
                minHeight: 45,
                height: 45,
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitBackgroundClip: 'text',
              },
            },
          }}
        />
        <TextField
          label="Description"
          name="description"
          value={form.description}
          onChange={handleChange}
          fullWidth
          inputProps={{ maxLength: 100 }}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#111',
              minHeight: 45,
              height: 45,
              '& fieldset': { borderColor: '#BDBDBD' },
              '&:hover fieldset': { borderColor: '#BDBDBD' },
              '&.Mui-focused fieldset': { borderColor: '#00C853' },
            },
            mb: 1.5,
            '& label.Mui-focused': { color: '#00C853' },
          }}
          InputProps={{
            sx: {
              background: '#fff',
              boxShadow: 'none',
              padding: 0,
              minHeight: 45,
              height: 45,
              '& input': {
                background: '#fff !important',
                boxShadow: 'none !important',
                padding: '0 8px',
                minHeight: 45,
                height: 45,
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitBackgroundClip: 'text',
              },
            },
          }}
        />
        {(editingRole && (editingRole.created_by || editingRole.created_at)) && (
          <Box sx={{ mt: 2, mb: 1, background: '#f8f9f6', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', p: 2, color: '#222', fontSize: 14, width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#4caf50', marginRight: 6 }}> ●</span>
              <span style={{ fontWeight: 600, marginRight: 6 }}>Created by:</span>
              <span style={{ marginRight: 12 }}>{getUserDisplay(editingRole.created_by)}</span>
              <span style={{ color: '#2196f3', fontSize: 13, marginRight: 3 }}> ⏰</span>
              <span>{formatDate(editingRole.created_at)}</span>
            </div>
            {editingRole.updated_by && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#ff9800', marginRight: 6 }}> ●</span>
                <span style={{ fontWeight: 600, marginRight: 6 }}>Last updated by:</span>
                <span style={{ marginRight: 12 }}>{getUserDisplay(editingRole.updated_by)}</span>
                <span style={{ color: '#2196f3', fontSize: 13, marginRight: 3 }}> ⏰</span>
                <span>{formatDate(editingRole.updated_at)}</span>
              </div>
            )}
          </Box>
        )}
        {formError && <Box sx={{ color: '#D32F2F', fontWeight: 500, fontSize: 15 }}>{formError}</Box>}
        {formSuccess && <Box sx={{ color: '#00C853', fontWeight: 500, fontSize: 15 }}>{formSuccess}</Box>}
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button type="submit" fullWidth variant="contained" sx={{ bgcolor: '#00C853', '&:hover': { bgcolor: '#009e46' }, minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1 }} disabled={isSubmitting}>
            {editingRole ? 'Update' : 'Add'}
          </Button>
          <Button type="button" fullWidth variant="outlined" sx={{ color: '#00C853', borderColor: '#00C853', minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1, '&:hover, &:active': { borderColor: '#009e46', color: '#009e46' } }} onClick={handleClear} disabled={isSubmitting}>
            Clear
          </Button>
          {editingRole && (
            <Button type="button" fullWidth variant="outlined" color="#00C853" onClick={onCancel} disabled={isSubmitting} sx={{color: '#00C853', borderColor: '#00C853', minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1 ,'&:hover, &:active': { borderColor: '#009e46', color: '#009e46' }}}>
              Cancel
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default RoleForm; 